import { Typography } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as NewTabIcon } from '@elementary/common-ui/assets/20x20/new-tab.svg';
import { ReactComponent as CloseIcon } from '@elementary/common-ui/assets/close.svg';
import { Flex, IconButton, SvgIcon } from '@elementary/common-ui/components';
import {
  extractTicketIdFromUrl,
  ticketingIconMap,
} from 'shared/components/ticketing/utils';
import type { IncidentTicketRow } from '../types';

const TicketRow = styled(Flex)`
  align-items: center;
  gap: 8px;
  padding: 3px 6px;
  height: 28px;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray5};
  justify-content: space-between;
  width: fit-content;
`;

const IndirectBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.palette.action.selected};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const serviceKey = (service: string) => service.toLowerCase().replace('_', '');

type Props = {
  tickets: IncidentTicketRow[];
  readOnly?: boolean;
  onUnlink?: (ticket: IncidentTicketRow) => void;
};

const IncidentTicketList = ({ tickets, readOnly, onUnlink }: Props) => {
  const showUnlink = !readOnly && onUnlink;

  const openTicket = (url: string) => window.open(url, '_blank');

  return (
    <>
      {tickets.map((ticket) => {
        const key = serviceKey(ticket.service);
        const icon = ticketingIconMap[key];
        const label =
          extractTicketIdFromUrl(
            ticket.service_ticket_url,
            key,
            ticket.service_ticket_id,
          ) || ticket.service_ticket_id;

        return (
          <TicketRow
            key={`${ticket.service_ticket_url}-${ticket.service_ticket_id}`}
          >
            <Flex alignItems="center" gap={1} sx={{ minWidth: 0 }}>
              {icon ? <SvgIcon width={16}>{icon}</SvgIcon> : null}
              <Typography sx={{ fontSize: '13px' }} noWrap>
                {label}
              </Typography>
              {ticket.is_indirect ? (
                <IndirectBadge>Indirect</IndirectBadge>
              ) : null}
            </Flex>
            <Flex alignItems="center" gap={0.2} sx={{ flexShrink: 0 }}>
              <IconButton
                size="small"
                aria-label="Open ticket"
                onClick={() => openTicket(ticket.service_ticket_url)}
              >
                <SvgIcon width={16} height={16} fillColor="gray4">
                  <NewTabIcon />
                </SvgIcon>
              </IconButton>
              {showUnlink && onUnlink ? (
                <IconButton
                  size="small"
                  aria-label="Unlink ticket"
                  onClick={() => onUnlink(ticket)}
                >
                  <SvgIcon width={12} height={12} strokeColor="gray4">
                    <CloseIcon />
                  </SvgIcon>
                </IconButton>
              ) : null}
            </Flex>
          </TicketRow>
        );
      })}
    </>
  );
};

export default IncidentTicketList;
